import { useDENTContext } from '../../../Services/DENTContext';
import { useCallback, useMemo } from 'react';
import {
	Tooth,
	ToothFurcation,
	ToothMobility,
	ToothPeriodontal,
} from '../../../../../graphql/graphql';
import useToothPeriodontalItem from './useToothPeriodontalItem';
import { DEFAULT_PERIODONTAL_DATA_MOCK } from '../../../Components/DENTWrapper/DENT_MOUTH_MOCK.const';

export default function useToothPeriodontal() {
	const { activeTooth, setActiveTooth } = useDENTContext();

	const periodontal = activeTooth?.periodontal ?? DEFAULT_PERIODONTAL_DATA_MOCK;

	const setPeriodontal = useCallback(
		(periodontal: ToothPeriodontal) => {
			setActiveTooth((prev: Tooth | undefined) => {
				if (!prev) return prev;
				return { ...prev, periodontal };
			});
		},
		[setActiveTooth],
	);

	const setMobility = useCallback(
		(mobility: ToothMobility) => {
			setPeriodontal({
				...periodontal,
				mobility:
					mobility === periodontal.mobility ? ToothMobility.None : mobility,
			});
		},
		[setPeriodontal, periodontal],
	);

	const setFurcation = useCallback(
		(furcation: ToothFurcation) => {
			setPeriodontal({
				...periodontal,
				furcation:
					furcation === periodontal.furcation ? ToothFurcation.None : furcation,
			});
		},
		[setPeriodontal, periodontal],
	);

	const commonProps = { periodontal, setPeriodontal };

	const { buccal } = useToothPeriodontalItem({ ...commonProps, key: 'buccal' });
	const { distoBuccal } = useToothPeriodontalItem({
		...commonProps,
		key: 'distoBuccal',
	});
	const { mesioBuccal } = useToothPeriodontalItem({
		...commonProps,
		key: 'mesioBuccal',
	});
	const { palatal } = useToothPeriodontalItem({
		...commonProps,
		key: 'palatal',
	});
	const { distoPalatal } = useToothPeriodontalItem({
		...commonProps,
		key: 'distoPalatal',
	});
	const { mesioPalatal } = useToothPeriodontalItem({
		...commonProps,
		key: 'mesioPalatal',
	});
	const { lingual } = useToothPeriodontalItem({
		...commonProps,
		key: 'lingual',
	});
	const { distoLingual } = useToothPeriodontalItem({
		...commonProps,
		key: 'distoLingual',
	});
	const { mesioLingual } = useToothPeriodontalItem({
		...commonProps,
		key: 'mesioLingual',
	});

	return useMemo(
		() => ({
			periodontal: {
				getPeriodontal: periodontal,
				buccal,
				distoBuccal,
				mesioBuccal,
				distoPalatal,
				mesioPalatal,
				palatal,
				lingual,
				distoLingual,
				mesioLingual,
				setMobility,
				setFurcation,
			},
		}),
		[activeTooth, periodontal],
	);
}
